import { Backdrop, Box, Button, ButtonGroup, Checkbox, Container, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, MenuItem, Radio, RadioGroup, Select, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography, useMediaQuery } from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import mainLogo from '../../images/logo_black_letters_2.png';
import React, { useEffect, useState } from 'react'
import AimdLinearBar from '../../components/progressBars/aimdLinearBar';
import { useQuestionnaireApi } from '../../api/demographics';
import { convertFeetAndInchesToCentimeters, convertPoundsToKilograms, convertKilogramsToPounds } from '../../utils/conversions';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@emotion/react';
import { PrivacyContent } from '../TermsAndPrivacy/privacyContent';
import ConfirmationDialog from '../../components/dialogbox/confirmationDialog';
import { TermsOfServiceContent } from '../TermsAndPrivacy/termsOfServiceContent';
import { VALIDATION_LIMITS } from '../../utils/miscellaneous';


//style for measurement TextField
const measurementLabelStyle = {
    '& .MuiInputLabel-root': {
        fontSize: "19px",
        fontWeight: "500",
        textAlign: "right",
        width: "100%",
        color: "#000000"
    },
}


const calculateValidRanges = ( userData ) => {
    // Constants for BMI limits
    const MIN_BMI = 10;
    const MAX_BMI = 65;

    let heightInCm;
    if ( userData.heightUnit === "cm" ) {
        heightInCm = Number( userData.height.cm );
    } else {
        heightInCm = convertFeetAndInchesToCentimeters( Number( userData.height.ft ), Number( userData.height.in ) );
    }

    // Calculate valid weight range for current height
    const heightInMeters = heightInCm / 100;
    let minWeight = MIN_BMI * ( heightInMeters * heightInMeters );
    let maxWeight = MAX_BMI * ( heightInMeters * heightInMeters );

    // Apply absolute limits in kg
    minWeight = Math.max( minWeight, VALIDATION_LIMITS.WEIGHT.KG.MIN );
    maxWeight = Math.min( maxWeight, VALIDATION_LIMITS.WEIGHT.KG.MAX );

    // Format weight ranges based on user's selected unit
    const weightRanges = {
        min: userData.weightUnit === "kg"
            ? Math.round( minWeight )
            : Math.round( convertKilogramsToPounds( minWeight ) ),
        max: userData.weightUnit === "kg"
            ? Math.round( maxWeight )
            : Math.round( convertKilogramsToPounds( maxWeight ) ),
        unit: userData.weightUnit
    };

    return {
        weightRanges,
        heightLimits: {
            min: userData.heightUnit === "cm"
                ? VALIDATION_LIMITS.HEIGHT.CM.MIN
                : `${ VALIDATION_LIMITS.HEIGHT.FT_IN.MIN.FT }'${ VALIDATION_LIMITS.HEIGHT.FT_IN.MIN.IN }"`,
            max: userData.heightUnit === "cm"
                ? VALIDATION_LIMITS.HEIGHT.CM.MAX
                : `${ VALIDATION_LIMITS.HEIGHT.FT_IN.MAX.FT }'${ VALIDATION_LIMITS.HEIGHT.FT_IN.MAX.IN }"`,
            unit: userData.heightUnit
        }
    };
};

const GuestDemographics2 = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const api = useQuestionnaireApi();
    const isLaptop = useMediaQuery( theme.breakpoints.up( 'lg' ) );
    const isSmallWidthScreen = useMediaQuery( '(max-width: 370px)' );
    const isSmallHeight = useMediaQuery( '(max-height:700px)' );
    const [ openModalPrivacy, setOpenModalPrivacy ] = useState( false );
    const [ openModalTerms, setOpenModalTerms ] = useState( false );
    const [ ethnicities, setEthnicities ] = useState( [] );
    const [ genders, setGenders ] = useState( [] );
    const [ isDobFirstInteraction, setIsDobFirstInteraction ] = useState( true );
    const [ currentStep, setCurrentStep ] = useState( 0 );
    const [ isTermsChecked, setIsTermChecked ] = useState( {
        agreeTerm1: false,
        agreeTerm2: false,
    } );
    const [ validation, setValidation ] = useState( {
        isDOBValidated: { isTrue: true, errorMsg: "" },
        isHeightValidated: { isTrue: true, errorMsg: "" },
        isWeightValidated: { isTrue: true, errorMsg: "" },
        isBMIValidated: { isTrue: true, errorMsg: "" },
    } );
    const [ confirmDialog, setConfirmDialog ] = useState( {
        title: '',
        subTitle: '',
        isOpen: false,
    } );
    const [ userData, setUserData ] = useState( {
        dateOfBirth: null,
        gender: '',
        weight: '',
        height: '',
        isFrontendHeightImperialUnits: true,
        isFrontendWeightImperialUnits: true,
        ethnicity: '',
        isSmoke: '',
        diabetes: '',
        isPregnant: null,
        isHighBP: '',
        bpMeds: '',
        hasHadHeartAttack: '',
        hasHadStroke: '',
        checkedAgreementInfo: false,
        checkedTermsAndPrivacy: false,
        heightUnit: "in",
        weightUnit: "lbs",
    } );

    useEffect( () => {
        api.getEthnicities().then( response => {
            setEthnicities( response.data );
        } );
        api.getGenders().then( response => {
            setGenders( response.data );
        } );
    }, [] );

    // Trigger validation  when userData.dateOfBirth changes, but skip on initial render
    useEffect( () => {
        if ( isDobFirstInteraction ) {
            return; // Skip validation on initial render
        }
        // Trigger validation after change
        const { validation: updatedValidation } = validateForm();
        setValidation( updatedValidation );
    }, [ userData.dateOfBirth, isDobFirstInteraction ] );

    //Handle agree terms & condition logic
    const handleCheckboxChange = ( event ) => {
        const { name, checked } = event.target;

        // Update the specific checkbox without affecting the others
        setIsTermChecked( ( prevState ) => ( {
            ...prevState,
            [ name ]: checked,
        } ) );
    };

    //handle final button and make API call
    const handleLetsGoPreScanBtn = async () => {
        if ( isTermsChecked.agreeTerm1 && isTermsChecked.agreeTerm2 ) {
            const { weight, height } = userData;
            await api.postQuestionnaire(
                userData.dateOfBirth,
                userData.gender,
                userData.ethnicity,
                userData.isFrontendHeightImperialUnits ? convertFeetAndInchesToCentimeters( Number( height.ft ), Number( height.in ) ) : Number( height.cm ),
                userData.isFrontendWeightImperialUnits ? convertPoundsToKilograms( Number( weight ) ) : Number( weight ),
                userData.isFrontendHeightImperialUnits,
                userData.isFrontendWeightImperialUnits,
                userData.isSmoke,
                userData.diabetes,
                userData.bpMeds,
                userData.bpMeds,
                userData.isPregnant,
                userData.hasHadStroke,
                userData.hasHadHeartAttack
            );

            navigate( '/guest-pre-face-scan' );
        }
    };

    //Handles action to close terms of use modal dialog
    const closeTermsModal = isClose => {
        setOpenModalTerms( isClose );
    };

    //Handles action to close privacy policy modal dialog
    const closePrivacyPolicyModal = isClose => {
        setOpenModalPrivacy( isClose );
    };

    //Handles action to open privacy policy modal dialog
    const handlePrivacyPolicy = () => {
        setOpenModalPrivacy( true );
    };

    //Handles action to open terms of use modal dialog
    const handleTermsOfUse = () => {
        setOpenModalTerms( true );
    };

    const validateForm = () => {
        let hasError = false;
        const updatedValidation = { ...validation };

        // Function to calculate age based on date of birth
        const calculateAge = ( dob ) => {
            const today = new Date();
            const birthDate = new Date( dob );
            let age = today.getFullYear() - birthDate.getFullYear();
            const m = today.getMonth() - birthDate.getMonth();
            if ( m < 0 || ( m === 0 && today.getDate() < birthDate.getDate() ) ) {
                age--;
            }
            return age;
        };

        // Function to calculate BMI
        const calculateBMI = ( weight, height ) => {
            // Convert height to meters
            const heightInMeters = height / 100;
            // Calculate BMI using formula: weight (kg) / (height (m))^2
            return weight / ( heightInMeters * heightInMeters );
        };

        // Step 1: Date of Birth / Age validation (13-120 years)
        if ( currentStep === 0 ) {
            if ( !userData.dateOfBirth ) {
                updatedValidation.isDOBValidated = { isTrue: false, errorMsg: "Please select your date of birth." };
                hasError = true;
            } else {
                const age = calculateAge( userData.dateOfBirth );
                if ( age < 13 || age > 120 ) {
                    updatedValidation.isDOBValidated = {
                        isTrue: false,
                        errorMsg: "Age must be between 13 and 120 years."
                    };
                    hasError = true;
                } else {
                    updatedValidation.isDOBValidated = { isTrue: true, errorMsg: "" };
                }
            }
        }

        // Step 2: Height & Weight validation
        if ( currentStep === 2 ) {
            // Height validation
            if ( !userData.height ) {
                updatedValidation.isHeightValidated = {
                    isTrue: false,
                    errorMsg: "Please enter your height."
                };
                hasError = true;
            } else {
                if ( userData.heightUnit === "in" ) {
                    const feet = Number( Math.round( userData.height.ft ) );
                    const inches = Number( Math.round( userData.height.in ) );

                    if ( !userData.height.ft || !userData.height.in ) {
                        updatedValidation.isHeightValidated = {
                            isTrue: false,
                            errorMsg: "Please enter both feet and inches."
                        };
                        hasError = true;
                    } else if (
                        feet < VALIDATION_LIMITS.HEIGHT.FT_IN.MIN.FT ||
                        ( feet === VALIDATION_LIMITS.HEIGHT.FT_IN.MAX.FT && inches > VALIDATION_LIMITS.HEIGHT.FT_IN.MAX.IN ) ||
                        feet > VALIDATION_LIMITS.HEIGHT.FT_IN.MAX.FT
                    ) {
                        updatedValidation.isHeightValidated = {
                            isTrue: false,
                            errorMsg: `Height must be between ${ VALIDATION_LIMITS.HEIGHT.FT_IN.MIN.FT }'${ VALIDATION_LIMITS.HEIGHT.FT_IN.MIN.IN }" and ${ VALIDATION_LIMITS.HEIGHT.FT_IN.MAX.FT }'${ VALIDATION_LIMITS.HEIGHT.FT_IN.MAX.IN }".`
                        };
                        hasError = true;
                    } else {
                        updatedValidation.isHeightValidated = { isTrue: true, errorMsg: "" };
                    }
                } else {
                    const heightInCm = Number( userData.height.cm );
                    if ( heightInCm < VALIDATION_LIMITS.HEIGHT.CM.MIN || heightInCm > VALIDATION_LIMITS.HEIGHT.CM.MAX ) {
                        updatedValidation.isHeightValidated = {
                            isTrue: false,
                            errorMsg: `Height must be between ${ VALIDATION_LIMITS.HEIGHT.CM.MIN } cm and ${ VALIDATION_LIMITS.HEIGHT.CM.MAX } cm.`
                        };
                        hasError = true;
                    } else {
                        updatedValidation.isHeightValidated = { isTrue: true, errorMsg: "" };
                    }
                }
            }

            // Weight validation
            if ( !userData.weight ) {
                updatedValidation.isWeightValidated = {
                    isTrue: false,
                    errorMsg: "Please enter your weight."
                };
                hasError = true;
            } else {
                if ( userData.weightUnit === "lbs" ) {
                    const weightInLbs = Number( userData.weight );
                    if ( weightInLbs < VALIDATION_LIMITS.WEIGHT.LBS.MIN || weightInLbs > VALIDATION_LIMITS.WEIGHT.LBS.MAX ) {
                        updatedValidation.isWeightValidated = {
                            isTrue: false,
                            errorMsg: `Weight must be between ${ VALIDATION_LIMITS.WEIGHT.LBS.MIN } lbs and ${ VALIDATION_LIMITS.WEIGHT.LBS.MAX } lbs.`
                        };
                        hasError = true;
                    } else {
                        updatedValidation.isWeightValidated = { isTrue: true, errorMsg: "" };
                    }
                } else {
                    const weightInKg = Number( userData.weight );
                    if ( weightInKg < VALIDATION_LIMITS.WEIGHT.KG.MIN || weightInKg > VALIDATION_LIMITS.WEIGHT.KG.MAX ) {
                        updatedValidation.isWeightValidated = {
                            isTrue: false,
                            errorMsg: `Weight must be between ${ VALIDATION_LIMITS.WEIGHT.KG.MIN } kg and ${ VALIDATION_LIMITS.WEIGHT.KG.MAX } kg.`
                        };
                        hasError = true;
                    } else {
                        updatedValidation.isWeightValidated = { isTrue: true, errorMsg: "" };
                    }
                }
            }

            // BMI validation (10-65)
            if ( !hasError && userData.height && userData.weight ) {
                let heightInCm = userData.heightUnit === "in" ?
                    convertFeetAndInchesToCentimeters(
                        Number( userData.height.ft ),
                        Number( userData.height.in )
                    ) :
                    Number( userData.height.cm );

                let weightInKg = userData.weightUnit === "lbs" ?
                    convertPoundsToKilograms( Number( userData.weight ) ) :
                    Number( userData.weight );

                const bmi = calculateBMI( weightInKg, heightInCm );

                if ( bmi < 10 || bmi > 65 ) {
                    const validRanges = calculateValidRanges( userData );
                    updatedValidation.isBMIValidated = {
                        isTrue: false,
                        errorMsg: `Your height and weight combination results in an invalid BMI (must be between 10 and 65). For your current height, please adjust your weight to be between ${ validRanges.weightRanges.min } and ${ validRanges.weightRanges.max } ${ validRanges.weightRanges.unit }.`
                    };
                    hasError = true;
                } else {
                    updatedValidation.isBMIValidated = { isTrue: true, errorMsg: "" };
                }
            }
        }

        return { isValid: !hasError, validation: updatedValidation };
    };

    // Handle Date of Birth change
    const handleDOBChange = ( newValue ) => {
        setUserData( ( prev ) => ( { ...prev, dateOfBirth: newValue } ) );

        // Mark the first interaction as done after the user selects a value
        if ( isDobFirstInteraction ) {
            setIsDobFirstInteraction( false ); // User has interacted with the input field
        }

    };

    const handleHeightChange = ( e, field ) => {
        const { value } = e.target;

        // Update the height field (either ft, in, or cm)
        setUserData( prev => ( {
            ...prev,
            height: { ...prev.height, [ field ]: value }  // Dynamically set the field value
        } ) );

        // Clear the error message for the specific field (ft, in, or cm)
        setValidation( prev => ( {
            ...prev,
            isHeightValidated: { isTrue: true, errorMsg: "" } // Clear general height validation error
        } ) );
    };

    const handleWeightChange = ( e ) => {
        const { value } = e.target;

        // Update the weight in userData
        setUserData( prev => ( {
            ...prev,
            weight: value // Update weight value
        } ) );

        // Clear the error message for weight validation
        setValidation( prev => ( {
            ...prev,
            isWeightValidated: { isTrue: true, errorMsg: "" } // Reset weight validation error
        } ) );
    };


    // Next step logic
    const handleNextStep = ( fieldRequiredValidation ) => {

        const { isValid, validation: updatedValidation } = validateForm( fieldRequiredValidation );

        // Set the validation state with the updated error messages
        setValidation( updatedValidation );

        // If the form is valid, proceed to the next step
        if ( isValid ) {
            setCurrentStep( prev => prev + 1 ); // Move to the next step
        }
    };

    //weight/Height unit change logic
    const handleUnitChange = ( event, newUnit, type ) => {
        if ( !newUnit ) return; // If no new unit is selected, do nothing

        setUserData( ( prevUserData ) => {
            const newState = { ...prevUserData };
            if ( type === "height" ) {
                newState.heightUnit = newUnit; // Update the height unit in userData
                newState.height = { ft: "", in: "", cm: "" }; // Reset height value
                newState.isFrontendHeightImperialUnits = newUnit === "in";
            } else if ( type === "weight" ) {
                newState.weightUnit = newUnit; // Update the weight unit in userData
                newState.weight = "";  // Reset weight value
                newState.isFrontendWeightImperialUnits = newUnit === "lbs";
            }
            return newState;
        } );
    };


    //update userData value state by passing field & value string
    const handleOptionSelect = ( field, value ) => {
        // First, update the specified field
        setUserData( prev => ( {
            ...prev,
            [ field ]: value
        } ) );

        // Special case for gender selection
        if ( currentStep === 1 && field === 'gender' && value !== 2 ) {
            setCurrentStep( prev => prev + 2 ); // Skip pregnancy question for non-females
        }
        // Special case for high blood pressure
        else if ( currentStep === 7 && field === 'isHighBP' && value === false ) {
            // If user doesn't have high blood pressure, automatically set bpMeds to false
            setUserData( prev => ( {
                ...prev,
                [ field ]: value,
                bpMeds: false // Automatically set BP medication to "No"
            } ) );
            setCurrentStep( prev => prev + 2 ); // Skip BP medication question if no high BP
        }
        else {
            setCurrentStep( prev => prev + 1 ); // Normal progression
        }
    };

    //this will set custmise style for height & weight measure type btn
    const toggleButtonStyles = {
        width: isLaptop ? "50px" : "32px",
        height: isLaptop ? "32px" : "27px",
        color: "#000000",
        fontSize: isLaptop ? "16px" : "12px",
        fontWeight: "500",
        "&.Mui-selected": {
            backgroundColor: "#72D8DA",
            color: "white",
        },
        "&.Mui-selected:hover": {
            backgroundColor: "#72D8DA",
            color: "white",
        },
    };

    const commonTypographyStyles = {
        fontSize: isLaptop ? "18px" : isSmallWidthScreen ? "12px" : "16px",
        fontWeight: 400,
        lineHeight: "27px",
        padding: "0px 5px",

    };

    const renderStep = () => {
        switch ( currentStep ) {
            case 0:
                return renderStep1();
            case 1:
                return renderStep2();
            case 2:
                return renderStep3();
            case 3:
                return renderStep4();
            case 4:
                return renderStep5();
            case 5:
                return renderStep6();
            case 6:
                return renderStep7();
            case 7:
                return renderStep8();
            case 8:
                return renderStep9();
            case 9:
                return renderStep10();
            case 10:
                return renderStep11();
            case 11:
                return renderStep12();
            default:
                return null;
        }
    };
    const CommonErrorMessage = ( { text } ) => {
        return (
            <Typography variant="caption" color="error" display="block" fontSize="16px" padding="10px 0px" gutterBottom>
                { text }
            </Typography>
        );
    };

    const renderStep1 = () => (
        <StepContainer title="What is your date of Birth ?" >
            <Stack width={ isLaptop ? "400px" : isSmallWidthScreen ? "100%" : "300px" } margin="40px 0px">
                <LocalizationProvider dateAdapter={ AdapterDayjs }>
                    <DatePicker
                        value={ userData.dateOfBirth }
                        onChange={ handleDOBChange }
                        disableFuture
                        sx={ { width: '100%' } }

                    />
                </LocalizationProvider>
                {
                    <CommonErrorMessage text={ !validation.isDOBValidated.isTrue && validation.isDOBValidated.errorMsg } />
                }
                <Box display="flex" alignItems="center" justifyContent="center" marginTop="50px">

                    <NextButtonDefault onClick={ () => handleNextStep( "dateOfBirth" ) } />

                </Box>
            </Stack>
        </StepContainer>
    )

    const renderStep2 = () => (
        <StepContainer title="What is your sex at birth ?">
            <Stack marginTop="10px" >

                {
                    genders.map( ( option, index ) => (
                        <DemographicOptionButton
                            key={ index }
                            option={ option.name }
                            onClick={ () => handleOptionSelect( 'gender', option.id ) }
                        />
                    ) )
                }

            </Stack>
        </StepContainer>
    )

    const renderStep3 = () => (
        <StepContainer title="What is your height & weight?" stepContainerSx={ { marginTop: "30px" } }>
            <Box sx={ { display: "flex", flexDirection: "column", width: isLaptop ? "450px" : isSmallWidthScreen ? "100%" : "300px", marginTop: "20px" } }>
                {/* Height Section */ }
                <Box marginBottom="20px">
                    <Stack display="flex" flexDirection="row" justifyContent="space-between" alignItems="center">
                        <Typography sx={ { mb: 1, fontSize: "17px", fontWeight: "500" } }>
                            Height
                        </Typography>
                        <ToggleButtonGroup
                            value={ userData.heightUnit }
                            exclusive
                            onChange={ ( event, newUnit ) => handleUnitChange( event, newUnit, "height" ) }
                            sx={ { mb: 2 } }
                        >
                            <ToggleButton value="in" sx={ toggleButtonStyles }>in</ToggleButton>
                            <ToggleButton value="cm" sx={ toggleButtonStyles }>cm</ToggleButton>
                        </ToggleButtonGroup>
                    </Stack>
                    { userData.heightUnit === "in" ? (
                        <Box sx={ { display: "flex", gap: 2 } }>
                            <TextField
                                label="ft"
                                variant="standard"
                                type="number"
                                value={ userData.height.ft }
                                sx={ measurementLabelStyle }
                                InputLabelProps={ { shrink: false } }
                                onChange={ ( e ) => handleHeightChange( e, 'ft' ) } // Call handler with field 'ft'
                                fullWidth />

                            <TextField
                                label="in"
                                variant="standard"
                                type="number"
                                sx={ measurementLabelStyle }
                                InputLabelProps={ {
                                    shrink: false, // Prevent the label from shrinking on focus
                                } }
                                value={ userData.height.in }
                                onChange={ ( e ) => handleHeightChange( e, 'in' ) }

                                fullWidth />
                        </Box>
                    ) : (
                        <TextField
                            label="cm"
                            variant="standard"
                            type="number"
                            fullWidth
                            sx={ measurementLabelStyle }
                            InputLabelProps={ { shrink: false } }
                            value={ userData.height.cm }
                            onChange={ ( e ) => handleHeightChange( e, 'cm' ) }

                        />

                    ) }
                </Box>

                {/* Weight Section */ }
                <Box>
                    <Stack display="flex" flexDirection="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6" sx={ { mb: 1, fontSize: "17px", fontWeight: "500" } }>
                            Weight
                        </Typography>
                        <ToggleButtonGroup
                            value={ userData.weightUnit }
                            exclusive
                            onChange={ ( event, newUnit ) => handleUnitChange( event, newUnit, "weight" ) }
                            sx={ { mb: 2 } }
                        >
                            <ToggleButton value="lbs" sx={ toggleButtonStyles }>lbs</ToggleButton>
                            <ToggleButton value="kg" sx={ toggleButtonStyles }>kg</ToggleButton>
                        </ToggleButtonGroup>
                    </Stack>
                    <TextField
                        label={ userData.weightUnit === "lbs" ? "lbs" : "kg" }
                        variant="standard"
                        type="number"
                        fullWidth
                        sx={ measurementLabelStyle }
                        InputLabelProps={ {
                            shrink: false, // Prevent the label from shrinking on focus
                        } }
                        value={ userData.weight }

                        onChange={ handleWeightChange }
                    />
                </Box>
                {
                    <CommonErrorMessage text={ !validation.isHeightValidated.isTrue && validation.isHeightValidated.errorMsg ||
                        !validation.isWeightValidated.isTrue && validation.isWeightValidated.errorMsg ||
                        !validation.isBMIValidated.isTrue && validation.isBMIValidated.errorMsg
                    } />
                }
            </Box>
            <Box display="flex" alignItems="center" justifyContent="center" marginTop="40px">

                <NextButtonDefault onClick={ () => handleNextStep( "heightWeight" ) } />

            </Box>

        </StepContainer>
    )

    const renderStep4 = () => (
        <StepContainer title="What is your ethnicity?" stepContainerSx={ { marginTop: "14px" } }>
            <Box >
                {
                    ethnicities.map( ( option, index ) => (
                        <DemographicOptionButton
                            optionBtnSx={ {
                                height: {
                                    xs: "48px",
                                    sm: "52px",
                                    md: "54px",
                                    lg: "54px"
                                }
                            } }
                            optionSx={ { fontSize: isLaptop ? "16px" : "14px" } }
                            key={ index }
                            option={ option.name }
                            onClick={ () => handleOptionSelect( "ethnicity", option.id ) }
                        />
                    ) ) }
            </Box>
        </StepContainer>
    );

    const renderStep5 = () => (
        <StepContainer title="Do you smoke ?" sx={ { marginTop: "100px" } }>
            { <Stack width="100%" >
                {
                    [
                        { label: "Yes", value: true },
                        { label: "No", value: false },

                    ].map( ( option, index ) => (
                        <DemographicOptionButton

                            key={ index }
                            option={ option.label }
                            onClick={ () => handleOptionSelect( 'isSmoke', option.value ) }
                        />

                    ) )
                }

            </Stack>

            }
        </StepContainer>
    )
    const renderStep6 = () => (
        <StepContainer title="Are you diabetic?" sx={ { marginTop: "150px" } }>
            { [
                { label: "Type 1", value: 1 },
                { label: "Type 2", value: 2 },
                { label: "No", value: 3 }
            ].map( ( option, index ) => (
                <DemographicOptionButton

                    key={ index }
                    option={ option.label }
                    onClick={ () => handleOptionSelect( "diabetes", option.value ) }
                />
            ) ) }
        </StepContainer>
    );

    //This will not render if male is slected
    const renderStep7 = () => (
        <StepContainer title="Are you pregnant or breastfeeding?" sx={ { marginTop: "100px" } }>
            { [
                { label: "Yes, I am pregnant.", value: true },
                { label: "Yes, I am breastfeeding.", value: true },
                { label: "No", value: false }
            ].map( ( option, index ) => (
                <DemographicOptionButton
                    key={ index }
                    option={ option.label }
                    onClick={ () => handleOptionSelect( 'isPregnant', option.value ) }
                />
            ) ) }
        </StepContainer>
    );

    const renderStep8 = () => (
        <StepContainer title="Do you have high blood pressure?" sx={ { marginTop: "150px" } }>
            { [
                { label: "Yes", value: true },
                { label: "No", value: false }
            ].map( ( option, index ) => (
                <DemographicOptionButton
                    key={ index }
                    option={ option.label }
                    onClick={ () => handleOptionSelect( "isHighBP", option.value ) }
                />
            ) ) }
        </StepContainer>
    );

    const renderStep9 = () => (
        <StepContainer title="Are you taking high blood pressure medication?" sx={ { marginTop: "150px" } }>
            <Stack display="flex" alignItems='center' justifyContent="center" width="100%">
                { [
                    { label: "Yes", value: true },
                    { label: "No", value: false }
                ].map( ( option, index ) => (
                    <DemographicOptionButton
                        key={ index }
                        option={ option.label }
                        onClick={ () => handleOptionSelect( "bpMeds", option.value ) }
                    />
                ) ) }
            </Stack>
        </StepContainer>
    );

    const renderStep10 = () => (
        <StepContainer title="Have you had a heart attack before?" sx={ { marginTop: "150px" } }>
            <Stack display="flex" alignItems='center' justifyContent="center" width="100%">
                { [
                    { label: "Yes", value: true },
                    { label: "No", value: false }
                ].map( ( option, index ) => (
                    <DemographicOptionButton
                        key={ index }
                        option={ option.label }
                        onClick={ () => handleOptionSelect( "hasHadHeartAttack", option.value ) }
                    />
                ) ) }
            </Stack>
        </StepContainer>
    );

    const renderStep11 = () => (
        <StepContainer title="Have you had a stroke before?" sx={ { marginTop: "150px" } }>
            { [
                { label: "Yes", value: true },
                { label: "No", value: false }
            ].map( ( option, index ) => (
                <DemographicOptionButton
                    key={ index }
                    option={ option.label }
                    onClick={ () => handleOptionSelect( "hasHadStroke", option.value ) }
                />
            ) ) }
        </StepContainer>
    );

    const renderStep12 = () => (
        <StepContainer

            btnTitle="Get my results!"
            stepContainerSx={ {
                marginTop: {
                    xs: "10px",
                    sm: "10px",
                    md: "20px",
                    lg: "50px"
                }
            } }
        >
            <Box>
                {/* 1st Terms checkbox */ }
                <Box>
                    <FormControlLabel
                        control={
                            <Checkbox
                                name="agreeTerm1"
                                checked={ isTermsChecked.agreeTerm1 }
                                onChange={ handleCheckboxChange }
                                color="primary"
                            />
                        }
                        label={
                            <Box alignItems="center" >
                                <Typography sx={ commonTypographyStyles } color="#000000"  >
                                    I understand the information provided in this assessment is not intended for the diagnosis treatment or prevention of disease, but rather to provide me general information about my health and products that may be able to improve my wellbeing.
                                </Typography>
                            </Box>
                        }
                    // Optional styling
                    />
                </Box>

                {/* 2nd terms checkbox */ }
                <Box sx={ {
                    pt: {
                        xs: "8px",
                        sm: "8px",
                        md: "14px",
                        lg: "25px"

                    }
                } }>
                    <FormControlLabel

                        control={
                            <Checkbox
                                name="agreeTerm2"
                                checked={ isTermsChecked.agreeTerm2 }
                                onChange={ handleCheckboxChange }
                                color="primary"
                            />
                        }
                        label={
                            <Box alignItems="center" >
                                <Typography color="#000000" sx={ commonTypographyStyles }>
                                    I have read and understood the
                                    <Button onClick={ handleTermsOfUse } sx={ {
                                        color: '#72D8DA', textDecoration: 'underline', fontWeight: "600", padding: "0px 6px",
                                        fontSize: isLaptop ? "18px" : isSmallWidthScreen ? "14px" : "16px"
                                    } } > terms of use</Button>
                                    <span style={ {
                                        color: '#000000', fontSize: {
                                            xs: "16px",
                                            sm: "16px",
                                            md: "16px",
                                            lg: "16px"
                                        }
                                    } }> and</span>
                                    <Button onClick={ handlePrivacyPolicy } sx={ {
                                        color: '#72D8DA', textDecoration: 'underline', fontWeight: "600", padding: "0px 6px",
                                        fontSize: isLaptop ? "18px" : isSmallWidthScreen ? "14px" : "16px"
                                    } } > privacy policy</Button>
                                </Typography>
                            </Box>
                        }

                    />

                </Box>
                <Box

                    sx={ {
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mt: {
                            xs: '30px',
                            sm: '30px',
                            md: '40px',
                            lg: "50px"
                        }

                    } }

                >

                    <NextButtonDefault btnText="Let's Go" onClick={ handleLetsGoPreScanBtn } />

                </Box>

            </Box>
        </StepContainer >
    );


    return (
        <Container sx={ {
            overflow: "hidden",
            height: isLaptop ? '100vh' : ( isSmallHeight ? "85vh" : "90vh" ),
            position: "relative",
            // width: "400px",
            width: isLaptop ? "600px" : isSmallWidthScreen ? "100%" : "350px"

        } } >
            <Box>
                <AimdLinearBar value={ ( currentStep + 1 ) * ( 100 / 12 ) } barHeight={ isLaptop ? "16px" : "10px" } />
            </Box>
            <Box sx={ {
                display: "flex",
                justifyContent: "center",
                alignItems: "center",

            } }>
                <Box>
                    { renderStep() }
                </Box>
            </Box>
            {/* Confirmation dialog box when user clicks Cancel button on footer */ }
            <ConfirmationDialog confirmDialog={ confirmDialog } setConfirmDialog={ setConfirmDialog } />
            {/* Modal box when user clicks terms of use link */ }
            <Dialog
                open={ openModalTerms }
                maxWidth="sm"
                closeAfterTransition
                BackdropComponent={ Backdrop }
                BackdropProps={ {
                    timeout: 500,
                } }
                PaperProps={ {
                    sx: {
                        borderRadius: '30px',
                        position: 'absolute',
                        mx: {
                            xs: 2,
                            md: 'auto',
                        },
                    },
                } }
                sx={ {
                    top: theme.spacing( 5 ),
                    paddingY: theme.spacing( 2 ),
                    '& .MuiDialog-paperScrollPaper': {
                        borderRadius: 5,
                    },
                } }
            >
                <DialogTitle sx={ { paddingY: theme.spacing( 3 ) } }>
                    <Typography component="span" variant="h6">
                        TERMS OF SERVICE AGREEMENT
                    </Typography>
                </DialogTitle>
                <DialogContent
                    sx={ {
                        paddingY: theme.spacing( 5 ),
                        paddingRight: theme.spacing( 3 ),
                    } }
                >
                    <TermsOfServiceContent />
                </DialogContent>
                <DialogActions sx={ { paddingBottom: theme.spacing( 2 ) } }>
                    <Button variant="text" onClick={ () => closeTermsModal( false ) }>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
            {/* Modal box when user clicks privacy policy link */ }
            <Dialog
                open={ openModalPrivacy }
                maxWidth="sm"
                closeAfterTransition
                BackdropComponent={ Backdrop }
                BackdropProps={ {
                    timeout: 500,
                } }
                PaperProps={ {
                    sx: {
                        borderRadius: '30px',
                        position: 'absolute',
                        mx: {
                            xs: 2,
                            md: 'auto',
                        },
                    },
                } }
                sx={ {
                    top: theme.spacing( 5 ),
                    paddingY: theme.spacing( 2 ),
                    '& .MuiDialog-paperScrollPaper': {
                        borderRadius: 5,
                    },
                } }
            >
                <DialogTitle sx={ { paddingY: theme.spacing( 3 ) } }>
                    <Typography component="span" variant="h6">
                        PRIVACY POLICY
                    </Typography>
                </DialogTitle>
                <DialogContent
                    sx={ {
                        paddingY: theme.spacing( 5 ),
                        paddingRight: theme.spacing( 3 ),
                    } }
                >
                    <PrivacyContent isPage={ false } />
                </DialogContent>
                <DialogActions sx={ { paddingBottom: theme.spacing( 2 ) } }>
                    <Button variant="text" onClick={ () => closePrivacyPolicyModal( false ) }>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

        </Container>

    )

}

const StepContainer = ( { title, children, stepContainerSx } ) => {
    const isLaptop = useMediaQuery( ( theme ) => theme.breakpoints.up( 'lg' ) );

    return (
        <Box sx={ {
            marginTop: "100px",
            ...stepContainerSx
        } }>
            <Typography
                sx={ {
                    mb: {
                        xs: "14px",
                        sm: "24px",
                        md: "30px",
                        lg: "30px"
                    },
                    fontSize: isLaptop ? "25px" : "20px",
                    fontWeight: "400",
                    textAlign: "center"

                } }
            >
                {
                    title
                }
            </Typography>

            <Stack>
                { children }
            </Stack>
            <Stack style={ {
                position: "fixed",
                bottom: 0,
                left: "50%",
                transform: "translateX(-50%)"
            } }>
                <img
                    src={ mainLogo }
                    alt="AIMD logo"

                    style={ {
                        width: isLaptop ? "100px" : "70px",
                        paddingBottom: isLaptop ? "20px" : "10px",
                        height: "auto",
                        // marginLeft: "10px",
                        userSelect: "none",
                        // paddingTop: "16px"
                    } }
                />
            </Stack>
        </Box>
    );
}

const DemographicOptionButton = ( { option, onClick, optionSx, optionBtnSx } ) => {
    const isLaptop = useMediaQuery( ( theme ) => theme.breakpoints.up( 'lg' ) );
    return (
        <Button
            sx={ {
                maxWidth: isLaptop ? "400px" : "300px",
                minWidth: isLaptop ? "400px" : "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "10px",
                height: {
                    xs: "60px",
                    sm: "60px",
                    md: "60px",
                    lg: "60px"

                },
                // marginY: "0.5rem",
                // padding : "0px",
                transition: "all 0.3s ease",
                color: "black",
                boxShadow: "rgba(0, 0, 0, 0.15) 0px 1px",


                "&:hover": {
                    backgroundColor: "#72D8DA", // Hover effect for selected and unselected
                    color: "white !important",
                    borderRadius: "40px",

                },
                ...optionBtnSx
            } }
            onClick={ onClick }>
            <Typography
                // color="#000"
                display="block"

                sx={ {
                    width: "100%",
                    fontSize: isLaptop ? "18px" : "15px",
                    fontWeight: "500",
                    lineHeight: "22.5px",
                    textAlign: "center",


                    ...optionSx

                } }>
                { option }
            </Typography>
        </Button>
    );
};


const NextButtonDefault = ( { btnText, onClick } ) => {
    const isLaptop = useMediaQuery( ( theme ) => theme.breakpoints.up( 'lg' ) );
    return <Button sx={ {
        // minWidth: isLaptop ? "248px" : "180px",
        minWidth: {
            xs: '150px',
            sm: '180px',
            md: '200px',
            lg: "248px"
        },
        minHeight: {
            xs: '20px',
            sm: '30px',
            md: '30px',
            lg: "30px"
        },
        // minHeight : "30px",
        padding: {
            xs: '8px 5px',
            sm: '12px 14px',
            md: '12px 14px',
            lg: "12px 12px"
        },
        background: "linear-gradient(92deg, #72d8da 2.79%, #35c6c9 90.54%)",
        // padding: "10px 50px",
        fontSize: "18px",
        boxShadow: "0px 12px 30px 6px rgba(0, 0, 0, 0.05), 0px 18px 40px 10px rgba(0, 0, 0, 0.07)",
        boxShadow: "0px 35px 35px 0px rgba(0, 0, 0, 0.09), 0px 9px 19px 0px rgba(0, 0, 0, 0.1)",
        borderRadius: "40px",
        color: "#fff",
        textAlign: "center",
        fontSize: {
            xs: '16px',
            sm: '16px',
            md: '20px',
            lg: "20px"
        },
        fontWeight: 600,
        "&:hover": {
            color: "#69D6D8",
            background: "white",
            border: "3px solid #72d8da !important"
        }
    }
    }
        onClick={ onClick }
    >
        { btnText ? btnText : "Next" }
    </Button>
}


export default GuestDemographics2